import {Rate} from "crqlar-react-common/dist/models/rate/rate.model";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {RateService} from "crqlar-react-common/dist/services/rate/rate.service";
import {RateCalculationService} from "crqlar-react-common/dist/services/rate/rateCalculation.service";
import {findRateByDate} from "crqlar-react-common/dist/utils/rate.util";
import {QueryClient, useQuery} from "react-query";
import {amountCentsOf} from "./serviceAutofill";
import {serviceRateIds} from "./serviceOption.util";

/** The rate a line's service has on the line's date. */
export interface LineRate {
    /** Carries the price on that date. */
    calculatedRate?: Rate
    /** The same rate's document, which carries the VAT. */
    rawRate?: Rate
    ratePriceCents?: number
}

const STALE_MS = 60 * 1000;

const sortedIds = (service?: Service) => [...(service?.rateIds ?? [])].sort();
const calculatedKey = (rateIds: string[], date?: Date) => ['folioLineRates', rateIds, date?.getTime()];
const rawKey = (rateIds: string[]) => ['folioLineRawRates', rateIds];

const calculate = (rateIds: string[], date: Date) => RateCalculationService.calculate({ids: rateIds, onDate: date});
const loadRaw = async (rateIds: string[]) => (
    await RateService.getAllRates({id: rateIds, limit: {offset: 0, limit: 1000}})
).content ?? [];

/** The rate on [date] - as the charge flow picks it - and its document; nothing without a rate that day. */
export const pickLineRate = (calculated: Rate[] | null | undefined, raw: Rate[] | undefined, date: Date): LineRate => {
    const calculatedRate = findRateByDate(calculated ?? undefined, date);
    if (!calculatedRate) return {};

    const rawRate = raw?.find(rate => rate.id === calculatedRate.id) ?? findRateByDate(raw, date);
    return {calculatedRate, rawRate, ratePriceCents: amountCentsOf(calculatedRate.price)};
};

export interface LineRateState extends LineRate {
    loading: boolean
    /** The service has rates at all. */
    hasRates: boolean
}

/** The line's rate for the info card and the hand-set mark; nothing is read without a service. */
export const useLineRate = (service: Service | undefined, date: Date | undefined, enabled = true): LineRateState => {
    const rateIds = sortedIds(service);
    const active = enabled && !!date && rateIds.length > 0;
    const options = {enabled: active, staleTime: STALE_MS, refetchOnWindowFocus: false};

    const calculated = useQuery(calculatedKey(rateIds, date), () => calculate(rateIds, date!), options);
    const raw = useQuery(rawKey(rateIds), () => loadRaw(rateIds), options);

    return {
        ...(active && date ? pickLineRate(calculated.data, raw.data, date) : {}),
        loading: active && (calculated.isLoading || raw.isLoading),
        hasRates: rateIds.length > 0,
    };
};

/**
 * The rates of all [services] on [date] in one calculation - for the prices the service picker lists
 * (pick them with `serviceOptionPriceCents`); nothing is read until the picker opens.
 */
export const useServicesRates = (services: Service[], date: Date | undefined, enabled: boolean) => {
    const rateIds = serviceRateIds(services);
    const active = enabled && !!date && rateIds.length > 0;
    const query = useQuery(
        ['folioPickerRates', rateIds, date?.getTime()],
        () => calculate(rateIds, date!),
        {enabled: active, staleTime: STALE_MS, refetchOnWindowFocus: false}
    );

    return {rates: query.data ?? undefined, loading: active && query.isLoading};
};

/** The same, read when a service is picked (shares the hook's cache). */
export const fetchLineRate = async (queryClient: QueryClient, service: Service, date: Date): Promise<LineRate> => {
    const rateIds = sortedIds(service);
    if (!rateIds.length) return {};

    const [calculated, raw] = await Promise.all([
        queryClient.fetchQuery(calculatedKey(rateIds, date), () => calculate(rateIds, date), {staleTime: STALE_MS}),
        queryClient.fetchQuery(rawKey(rateIds), () => loadRaw(rateIds), {staleTime: STALE_MS}),
    ]);
    return pickLineRate(calculated, raw, date);
};
